import { useEffect } from 'react';
import { useOutletContext, useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { useAppSelector } from '@store';
import { PermissionEnum } from '@ui/src';
import { format } from 'date-fns';
import Safeguarding from '../../components/safeguarding/safeguarding';

import cn from './safeguarding.module.css';

const SafeguardingFiat = () => {
  const { excess, accountsCategories, safeguarding, itemsVisible } =
    useAppSelector((state) => state.safeguarding);
  const { activePermissions } = useAppSelector((state) => state.root);

  const { search } = useLocation();

  const navigate = useNavigate();

  const {
    setExcludeRevaluation,
    setIncludeSegregatedAccounts,
    isLoading,
    isFetching,
    setPrecision,
    setDates,
    setDownloadReportDates,
    getSafeguardingReportAction,
    errorList,
  } = useOutletContext<{
    setExcludeRevaluation: (data: boolean) => void;
    setIncludeSegregatedAccounts: (data: boolean) => void;
    setPrecision: (data: string) => void;
    setDates: (data: Date[]) => void;
    setDownloadReportDates: (data: Date[]) => void;
    errorList?: string;
    getSafeguardingReportAction: (data: string) => void;
    isLoading?: boolean;
    isFetching?: boolean;
  }>();

  useEffect(() => {
    let title = 'Safeguarding Fiat report';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  useEffect(() => {
    if (
      activePermissions &&
      activePermissions?.includes(PermissionEnum.REPORTS_SAFEGUARDING_LIST) &&
      search &&
      accountsCategories
    ) {
      const parsed = queryString.parse(search);

      const parsedDates: Date[] = [];

      if (parsed?.transferDateFrom) {
        parsedDates.push(new Date(parsed?.transferDateFrom as string));
      }

      if (parsed.transferDateTo) {
        parsedDates.push(new Date(parsed?.transferDateTo as string));
      }

      if (parsed?.excludeRevaluation) {
        setExcludeRevaluation(Boolean(parsed?.excludeRevaluation === 'true'));
      }

      if (parsed?.includeSegregatedAccounts) {
        setIncludeSegregatedAccounts(
          Boolean(parsed?.includeSegregatedAccounts === 'true'),
        );
      }

      if (parsed.roundDecimals) {
        setPrecision(parsed.roundDecimals as string);
      }

      setDates(parsedDates);
      setDownloadReportDates(parsedDates);

      getSafeguardingReportAction(search);
    }
  }, [activePermissions, search, accountsCategories]);

  useEffect(() => {
    if (
      activePermissions &&
      activePermissions?.includes(PermissionEnum.REPORTS_SAFEGUARDING_LIST) &&
      !search &&
      accountsCategories
    ) {
      const date = new Date();

      date.setHours(0);
      date.setMinutes(0);
      date.setSeconds(0);
      date.setMilliseconds(0);

      setDates([new Date(date)]);
      setDownloadReportDates([new Date(date)]);

      navigate(`?transferDateFrom=${format(new Date(date), 'yyyy-MM-dd')}`);
    }
  }, [activePermissions, search, accountsCategories]);

  return (
    <div className={cn.safeguardingSheet}>
      {errorList && (
        <div className={cn.reportsLoadingError}>
          <div className={cn.reportsLoadingErrorTitle}>Unable to load data</div>
          <div className={cn.reportsLoadingErrorText}>{errorList}</div>
        </div>
      )}
      {!errorList && (
        <Safeguarding
          itemsVisible={itemsVisible}
          items={safeguarding}
          excess={excess}
          isLoading={isLoading || isFetching || !accountsCategories}
          search={search}
        />
      )}
    </div>
  );
};

export default SafeguardingFiat;
